import { LocationDescriptorObject } from "history";
import React from "react";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { InjectedRouter, Link } from "react-router";
import { push } from "react-router-redux";
import { Dispatch } from "redux";
import tinycolor from "tinycolor2";
import { AbtaLogo, AtolLogo, FlowBox, LinkButton, Loader } from "../../../../core/atoms/atoms";
import { CardContainer } from "../../../../shared/components/inspiration/cardContainer";
import { apiPromiseHook, createEnterHookPromise } from "../../../../shared/flux/actions/enterHooks";
import { CollectionKeyHelper } from "../../../../shared/helpers/contentCollectionHelpers";
import { buildMetaTags, buildStructuredDataSnippet } from "../../../../shared/helpers/metaData";
import { MatchState } from "../../../../shared/helpers/reduxHelpers";
import { parseColourString } from "../../../../../../packages/ui/src/utils/uiHelpers";
import ResponsiveContainer from "../../../../shared/helpers/responsiveContainer";
import { StoreActionFactory } from "../../../../shared/state/storeActionFactory";
import { CallToActionBlock } from "../../../components/ctaBlock/ctaBlock";
import { LocationNavigation } from "../../../components/location/locationNavigation";
import { HeroSection, CountryHeader } from "@travellocal/ui"
import { SwipeCarousel } from "../../../components/swipeCarousel/swipeCarousel";
import { ActionPromises } from "../../../state/actionPromises";
import * as WebRoutes from "@travellocal/utils/webRoutes";

import "./countryIndex.scss";

interface StateProps {
  relatedContentState: IState<IContentCollection<IContentBlogPost>>;
  countryState: IState<ICountry>;
  location?: LocationDescriptorObject;
}

interface DispatchProps {
  dispatch: any;
  navigate: (route: string) => void;
}

interface OwnProps {
  router: InjectedRouter;
}

type Props = StateProps & DispatchProps & OwnProps;

const dispatchToProps = (dispatch: Dispatch<IWebStoreState>): DispatchProps => ({
  dispatch,
  navigate: (route: string) => dispatch(push(route)),
});

const stateToProps = (state: IWebStoreState): StateProps => {
  const countryState = state.country;
  let relatedContentState: IState<IContentCollection<IContentBlogPost>>;

  if (countryState.data) {
    const contentState = state.contentByCollection;
    const sectionCollection = contentState.data[CollectionKeyHelper.LocationRelated(countryState.data.id)];
    relatedContentState = sectionCollection as IState<IContentCollection<IContentBlogPost>>;
  }

  return {
    countryState,
    relatedContentState,
  };
};

class CountryIndexComponent extends React.Component<Props> {

  private countryStructuredData = () => {
    const country = this.props.countryState.data;

    const structuredData = {
      name: country.name,
      image: country.heroImage ? country.heroImage.largeUrl : "",
      geo: {
        "@type": "GeoCoordinates",
        "latitude": country.geoLocation.latitude,
        "longitude": country.geoLocation.longitude,
      },
    };
    return buildStructuredDataSnippet("Country", structuredData);
  }

  private renderIndex = (country: ICountry) => {
    const meta = buildMetaTags(country, `${globalConfig.wwwBaseUrl}${WebRoutes.country(country.slug, country.parent.slug)}`, country.heroImage);

    // Colour certain page elements with the hero image colour
    const rootStyle: React.CSSProperties = {};
    const accentColour = country.heroImage ? parseColourString(country.heroImage.colour) : undefined;

    if (accentColour) {
      const c: tinycolor.ColorFormats.RGBA = tinycolor(accentColour).toRgb();
      rootStyle["--page-accent"] = accentColour;
      rootStyle["--page-accent-rgb"] = `${c.r}, ${c.g}, ${c.b}`;
    }

    // #1531 Enable selection of footer image
    const footerImage = country.images.find(x => x.locationImageType === LocationImageType.Footer) || country.heroImage;
    return (
      <div className="country-index" style={rootStyle}>
        <Helmet
          title={country.titleTag || country.name}
          meta={meta}

          script={[
            this.countryStructuredData(),
          ]}
          link={[
            { rel: "canonical", href: `${globalConfig.wwwBaseUrl}${WebRoutes.country(country.slug, country.parent.slug)}` },
          ]}
        />

        <CountryHeader country={country} />

        <LocationNavigation
          items={this.generateNavItems(country)}
          onSelectChange={route => {
            this.props.router.push(route);
          }}
        />

        {this.props.children}

        {MatchState(this.props.relatedContentState, {
          data: blogs => (blogs && blogs.length > 0) && (
            <div className="blog-cards-wrap">
              <div className="center-wrap">
                <h2>Read all about it</h2>
              </div>

              <ResponsiveContainer
                mobile={() => (
                  <CardContainer
                    collectionKey={CollectionKeyHelper.LocationRelated(this.props.countryState.data.id)}
                    cardLimit={4}
                    filters={{ types: [ContentType.BlogPost] }}
                    className="width-wrapper"
                    renderCardWrapper={elements => (
                      <SwipeCarousel>{elements}</SwipeCarousel>
                    )}
                  />
                )}
                tablet={() => (
                  <div className="columns is-centered is-marginless">
                    <div className="column is-10">
                      <CardContainer
                        collectionKey={CollectionKeyHelper.LocationRelated(this.props.countryState.data.id)}
                        cardLimit={4}
                        className="columns blog-cards"
                        columnClassName="column is-6-tablet is-4-desktop"
                        filters={{ types: [ContentType.BlogPost] }}
                        hasHeroCard={count => count >= 4}
                      />
                    </div>
                  </div>
                )}
              />

              <div className="center-wrap">
                <LinkButton
                  to={WebRoutes.inspiration({ types: [ContentType.BlogPost], locations: [this.props.countryState.data.id] })}
                  buttonClass="secondary-button">
                  Read more articles</LinkButton>
              </div>
            </div>
          ),
        })}

        <div className="country-cta-block">
          <CallToActionBlock
            backgroundImage={footerImage}
            alt={footerImage ? footerImage.meta : null}
            linkUrl={WebRoutes.enquiryForm({ locationSlug: country.slug, campaign: "country-page-header" })}
            bodyTitle="Start planning your perfect tailor-made trip!"
            // tslint:disable-next-line: max-line-length
            bodyText={[`Do you already have an idea of what your dream holiday in ${country.name} entails? Whether you're ready to book or would like your ideas to be fined-tuned into something more, send an enquiry and our trusted local experts will design your perfect tailor-made holiday.`]}
            boxAlignX="left"
            boxAlignY="top"
          >
            <div className="cta-block-button">
              <LinkButton
                to={WebRoutes.enquiryForm({ locationSlug: country.slug, campaign: "country-page-footer" })}
                className="primary-button heading--4 text--light"
              >
                Plan my trip
              </LinkButton>
            </div>

            <div className="country-cta-block__trust">
              <div className="country-cta-block__trust__logos">
                <AbtaLogo type="colour" height={45} />
                <AtolLogo type="dark" width={70} />
              </div>
              <LinkButton to={WebRoutes.aboutHowItWorks()} buttonClass="secondary-button" className="text--dark">
                How does TravelLocal work?
              </LinkButton>
            </div>
            <div className="clearfix" />
          </CallToActionBlock>
        </div>
      </div>
    );
  }

  private generateNavItems = (country) => {
    const navItems = [
      {
        to: WebRoutes.country(country.slug, country.parent.slug),
        onlyActiveOnIndex: true,
        title: "Overview",
      },
      {
        to: WebRoutes.countryExperts(country.slug, country.parent.slug),
        title: "Our Experts",
      },
      {
        to: WebRoutes.countryTripIdeas(country.slug, country.parent.slug),
        title: "Trip Ideas",
      },
      {
        to: WebRoutes.countryReviews(country.slug, country.parent.slug),
        title: "Reviews",
      },
    ];

    if (country.images.length !== 0) {
      navItems.push({
        to: WebRoutes.countryGallery(country.slug, country.parent.slug),
        title: "Gallery",
      });
    }

    return navItems;
  }

  public render() {
    const { countryState } = this.props;

    return MatchState(countryState, {
      pending: () => <Loader />,
      error: () => (
        <div className="width-wrapper p-top-large">
          <h1>Sorry! Something went wrong and we can't show you this page.</h1>
          <p><Link to={WebRoutes.destinations()}>Click here to see all the destinations we offer</Link></p>
        </div>
      ),
      ok: () => this.renderIndex(countryState.data),
    }) as JSX.Element;
  }
}

export const Index = connect(stateToProps, dispatchToProps)(CountryIndexComponent);

export const indexOnEnter = apiPromiseHook<any>(
  createEnterHookPromise((router) => {
    const countrySlug = router.params.countrySlug;

    return [
      // TODO - server render doesn't seem to wait for thunk to complete
      StoreActionFactory.thunk<IWebStoreState>(async (dispatch, getState) => {
        const { country, lto } = getState();

        if (!country
          || !lto.data
          || !country.data
          || country.data.slug !== countrySlug
          || (country.data.defaultLto && lto.data.id !== country.data.defaultLto.id)) {

            // send 404 response if country is not found
            const should404 = true;

          await dispatch(ActionPromises.countryAndDefaultLtoFromSlug(countrySlug, should404));
        }

        const updatedCountry = getState().country.data;
        if (updatedCountry) {
          return dispatch(ActionPromises.getContentPagesForCountry({ locations: [updatedCountry.id] }));
        }
      }),

      ActionPromises.getLocationSections(countrySlug),
    ];
  }),
);
